import { useMemo } from 'react';
import { User, TimesheetLogType, UserRole } from '../../../types';
import { calculateDaysBetweenDates, getMonthLabelShort } from '../../../utils/formatters';

export interface OpenTimesheetApprovalTableRow {
  userId: number;
  tsId: number;
  timesheetName: string;
  status: 'On track' | 'Needs Attention' | 'Critical';
  submittedOn?: string;
  cycle?: number;
  duration?: number;
  pendingWith?: {
    details: string;
    users: string[];
    role: UserRole;
  };
  pendingDays?: number;
}

export interface ClosedTimesheetApprovalTableRow {
  userId: number;
  tsId: number;
  timesheetName: string;
  submittedOn?: string;
  closedOn?: string;
  status?: string;
  cycle?: number;
  duration?: number;
}

export function useAllOpenLogsMapped(operatingPartners?: User[]) {
  const opLogs = useMemo(() => {
    if (!operatingPartners) return [];

    const tableData: OpenTimesheetApprovalTableRow[] = [];
    operatingPartners.forEach((user) => {
      if (!user.monthlyTimesheets) return;

      user.monthlyTimesheets.forEach((timesheet) => {
        if (!timesheet.timesheetLogs || timesheet.timesheetLogs.length === 0) return;

        const timesheetName = `${user.name} - ${getMonthLabelShort(timesheet.month)} ${
          timesheet.year
        }`;
        const sortedLogs = [...timesheet.timesheetLogs].sort(
          (a, b) => new Date(a.actionStart).getTime() - new Date(b.actionStart).getTime()
        );

        const firstLog = sortedLogs[0];

        let status: 'On track' | 'Needs Attention' | 'Critical' = 'On track';

        const nowIso = new Date().toISOString();
        const duration = calculateDaysBetweenDates(firstLog.actionStart, nowIso);

        if (duration < 5) {
          status = 'On track';
        } else if (duration >= 5 && duration <= 12) {
          status = 'Needs Attention';
        } else {
          status = 'Critical';
        }

        const opSubmissionLog = timesheet.timesheetLogs.find(
          (log) => log.role === UserRole.OPERATING_PARTNER
        );

        const lastCycle = Math.max(...timesheet.timesheetLogs.map((log) => log.cycle));

        const pendingLogs = timesheet.timesheetLogs.filter(
          (log) => log.type === TimesheetLogType.PENDING
        );

        let pendingWith: OpenTimesheetApprovalTableRow['pendingWith'];
        let pendingDays: number | undefined;

        if (pendingLogs.length > 0) {
          const role = pendingLogs[0].role;
          const pendingUsers: string[] = pendingLogs.map((log) => log.createdBy?.name || '');

          const pendingCount = pendingUsers.length;
          const totalCount = timesheet.timesheetLogs.filter(
            (log) => log.role === pendingLogs[0].role
          ).length;

          const details = pendingUsers.length > 1 ? `(${pendingCount}/${totalCount})` : '';

          pendingWith = {
            details,
            users: pendingUsers,
            role,
          };

          pendingDays = calculateDaysBetweenDates(
            pendingLogs[0].actionStart,
            new Date().toISOString()
          );
        }

        tableData.push({
          userId: user.id,
          tsId: timesheet.id,
          timesheetName,
          status,
          submittedOn: opSubmissionLog?.actionEnd,
          cycle: lastCycle,
          duration,
          pendingWith,
          pendingDays,
        });
      });
    });

    return tableData.sort((a, b) => (b.duration || 0) - (a.duration || 0));
  }, [operatingPartners]);

  return opLogs;
}

export function useClosedLogsMapped(operatingPartners?: User[]) {
  const opLogs = useMemo(() => {
    if (!operatingPartners) return [];

    const tableData: ClosedTimesheetApprovalTableRow[] = [];
    operatingPartners.forEach((user) => {
      if (!user.monthlyTimesheets) return;

      user.monthlyTimesheets.forEach((timesheet) => {
        if (!timesheet.timesheetLogs || timesheet.timesheetLogs.length === 0) return;

        const timesheetName = `${user.name} - ${getMonthLabelShort(timesheet.month)} ${
          timesheet.year
        }`;
        const sortedLogs = [...timesheet.timesheetLogs].sort(
          (a, b) => new Date(a.actionStart).getTime() - new Date(b.actionStart).getTime()
        );

        const firstLog = sortedLogs[0];
        const lastLog = sortedLogs[sortedLogs.length - 1];

        const duration = calculateDaysBetweenDates(firstLog.actionStart, lastLog.actionEnd);

        const opSubmissionLog = timesheet.timesheetLogs.find(
          (log) => log.role === UserRole.OPERATING_PARTNER
        );

        const lastCycle = Math.max(...timesheet.timesheetLogs.map((log) => log.cycle));

        tableData.push({
          userId: user.id,
          tsId: timesheet.id,
          timesheetName,
          status: timesheet.status,
          submittedOn: opSubmissionLog?.actionEnd,
          closedOn: lastLog.actionEnd,
          cycle: lastCycle,
          duration,
        });
      });
    });

    return tableData.sort((a, b) => (b.duration || 0) - (a.duration || 0));
  }, [operatingPartners]);

  return opLogs;
}
